import { apiErrorMessage, customFetch } from '@agos/api-client'
import {
  getGetEmployeesExternalDriversQueryKey,
  useGetEmployeesExternalDrivers,
  usePatchEmployeesIdApprove,
} from '@agos/api-client/hr'
import { courierBalanceOf, courierBalancePath, formatCents, formatDecimal, formatMoney, T, tt } from '@agos/shared'
import { EmptyState, PageHeader, StatCard, cn, toast } from '@agos/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Banknote,
  Copy,
  Euro,
  HandCoins,
  Link2,
  MailCheck,
  MapPin,
  Package,
  ShieldAlert,
  ShieldCheck,
  Truck,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { CourierSettleDialog } from '../dispatch/CourierBoard'
import {
  approvalUnblocks,
  balanceStateNote,
  courierSignupUrl,
  driverGate,
  externalTotals,
  periodRange,
  type ExternalDriver,
  type Period,
} from './external-driver'

/**
 * `/external-driver` — couriers who are not employees, paid per delivery.
 *
 * **§9.2 said this feature was broken, and it was.** The diagnosis is in
 * `docs/pages/manager-delivery/external-driver.md`: `is_external` had no write
 * path in either backend, and the self-registration that once set it belonged to
 * the retired driver app. So this screen managed a set that was always empty, and
 * `GET /employees/external-drivers` returned `[]` against the production dumps.
 *
 * **The write path landed 2026-08-05** — `EmployeeService` names `is_external` in
 * its `INSERT` citing §9.2, and §9.2's journey e2e seeds a driver and walks it.
 * The list is still `[]` on the restored dumps because no franchise has created
 * one, which now means *nobody has signed up yet* rather than *nobody can*. Those
 * two look identical on screen, which is why this paragraph is worth keeping
 * accurate rather than deleting.
 *
 * The screen itself is correct and is ported faithfully. Its siblings
 * `index.ts` and `external-driver.ts` carried the same stale claim and were
 * corrected on 2026-08-05; this one survived that sweep because the phrase
 * *"no write path"* wraps across a line here and a line-based grep cannot see it.
 */

const PERIODS: Array<{ id: Period; label: string }> = [
  { id: 'today', label: tt('Dnes') },
  { id: 'week', label: tt('Týždeň') },
  { id: 'month', label: tt('Mesiac') },
  { id: 'custom', label: tt('Vlastné') },
]

export function ExternalDriverPage() {
  const queryClient = useQueryClient()
  const [period, setPeriod] = useState<Period>('month')
  const [custom, setCustom] = useState({ from: '', to: '' })

  const range = useMemo(() => periodRange(period, custom, new Date()), [period, custom])
  /* FB-841, §9.555 - **`isError`, because the last card is labelled *Na výplatu*.** These couriers are paid per
     delivery, so a failed read put `formatCents(0)` against somebody's wages beside *nikto nerozvážal* - §3.152's
     partner payout, moved to the manager. */
  const {
    data,
    isLoading,
    isError: driversFailed,
    refetch: refetchDrivers,
  } = useGetEmployeesExternalDrivers({ from: range.from, to: range.to })

  const drivers = useMemo(
    () => (Array.isArray(data) ? (data as ExternalDriver[]) : []),
    [data],
  )
  const totals = externalTotals(drivers)

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: getGetEmployeesExternalDriversQueryKey() })

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Externí šoféri')}
        description={tt('Kuriéri platení za rozvoz.')}
        icon={<Truck className="w-5 h-5" />}
      />

      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex rounded-xl border border-border overflow-hidden">
          {PERIODS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setPeriod(option.id)}
              className={cn(
                'px-3 py-1.5 text-xs font-bold transition-colors',
                period === option.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {period === 'custom' && (
          <>
            <input
              type="date"
              value={custom.from}
              onChange={(event) => setCustom((current) => ({ ...current, from: event.target.value }))}
              aria-label={tt('Od')}
              className="px-3 py-1.5 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
            />
            <input
              type="date"
              value={custom.to}
              onChange={(event) => setCustom((current) => ({ ...current, to: event.target.value }))}
              aria-label={tt('Do')}
              className="px-3 py-1.5 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
            />
          </>
        )}
      </div>

      <SignupLink />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label={tt('Šoférov')} value={String(totals.driverCount)} icon={Truck} color="violet" />
        <StatCard
          label={tt('Rozvozov')}
          value={String(totals.deliveries)}
          icon={Package}
          color="blue"
        />
        <StatCard
          label={tt('Kilometrov')}
          value={`${formatDecimal(totals.kilometres, 1)} km`}
          icon={MapPin}
          color="emerald"
        />
        {/* §9.786 - *Odmeny spolu*, which is what the day's closing records as one *Výber* (§10A.438): the couriers
            kept their reward out of the cash they paid at pickup, or were paid it from the drawer. */}
        <StatCard
          label={tt('Odmeny spolu')}
          value={formatCents(totals.rewardCents)}
          icon={Euro}
          color="amber"
        />
        <StatCard
          label={tt('Hotovosť pri dverách')}
          value={formatCents(totals.cashCents)}
          icon={Banknote}
          color="emerald"
        />
      </div>
      <p className="text-[11px] text-muted-foreground" data-rewards-closing-note>
        <T>
          Odmeny za deň zapíšte pri dennej uzávierke ako jeden Výber z kasy - šofér si ich nechal z hotovosti alebo ste
          mu ich vyplatili.
        </T>
      </p>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((placeholder) => (
            <div
              key={placeholder}
              className="h-20 rounded-2xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : driversFailed ? (
        /* Before the empty state, because `drivers` is empty on a failure too. */
        <div className="bg-card border border-destructive/40 rounded-2xl p-10" data-external-drivers-error>
          <EmptyState
            icon={<Truck className="w-10 h-10" />}
            title={tt('Prehľad sa nepodarilo načítať')}
            description={tt('Nevieme, kto rozvážal ani čo je na výplatu - server neodpovedal.')}
            action={
              <button type="button" onClick={() => void refetchDrivers()} data-external-drivers-retry className="px-2.5 py-1 rounded-lg border border-border font-semibold text-foreground text-sm">
                <T>Skúsiť znova</T>
              </button>
            }
          />
        </div>
      ) : drivers.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-10 space-y-3">
          <EmptyState
            icon={<Truck className="w-10 h-10" />}
            title={tt('Žiadni externí šoféri')}
            description={tt('V tomto období nikto z externých kuriérov nerozvážal.')}
          />
          {/* §9.2, then §9.786. This said nobody could sign themselves up, which was true until the owner's
              answer 7 (*the driver signs up and the restaurant approves*): the sign-up link above is how a courier
              arrives now. Marking an existing employee is still the other way in, and still said. */}
          <p className="text-[11px] text-muted-foreground text-center max-w-lg mx-auto">
            <T>
              Kuriér sa zaregistruje cez odkaz vyššie a vy ho tu schválite. Existujúceho zamestnanca s rolou šofér
              označíte v Zamestnancoch zaškrtnutím „Externý vodič“.
            </T>
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {drivers.map((driver) => (
            <DriverCard key={driver.id} driver={driver} onChanged={refresh} />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * The link to send a courier - §9.786, owner answer 7. Copied rather than only shown, because it is sent by message.
 */
function SignupLink() {
  const url = courierSignupUrl(typeof window === 'undefined' ? '' : window.location.origin)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast({ title: tt('Odkaz skopírovaný'), description: url })
    } catch {
      toast({ title: tt('Skopírujte odkaz ručne'), description: url })
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-4 py-3" data-courier-signup-link>
      <Link2 className="w-4 h-4 text-primary shrink-0" />
      <span className="text-sm">
        <T>Registrácia pre kuriéra</T>:
      </span>
      <code className="text-xs bg-muted/50 rounded px-1.5 py-0.5 break-all">{url}</code>
      <button
        type="button"
        onClick={() => void copy()}
        className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-lg border border-border text-xs font-semibold"
      >
        <Copy className="w-3.5 h-3.5" /> <T>Kopírovať</T>
      </button>
    </div>
  )
}

/**
 * One courier: the period's deliveries, reward and cash, what is owed between them now, and approval.
 *
 * **The per-driver rate editor is gone** - the owner's answer 5, *one model for all external drivers*: the reward is
 * the rule on *Nastavenia rozvozu*, fixed on each order. The stored rates still price an order from before
 * §10A.438, which is why the server keeps reading them.
 */
function DriverCard({
  driver,
  onChanged,
}: {
  driver: ExternalDriver
  onChanged: () => void
}) {
  const [settling, setSettling] = useState(false)
  const gate = driverGate(driver)
  const canUnblock = approvalUnblocks(driver)
  const setApproved = usePatchEmployeesIdApprove()
  const balanceRead = useQuery({
    queryKey: ['courier-balance', driver.id ?? 0],
    queryFn: () => customFetch<unknown>(courierBalancePath(driver.id ?? 0)),
    enabled: driver.id !== undefined,
  })
  const balance = courierBalanceOf(balanceRead.data)

  const setApproval = async (approved: boolean) => {
    if (driver.id === undefined) return
    try {
      await setApproved.mutateAsync({ id: driver.id, data: { approved } })
      toast({
        title: approved ? tt('Šofér schválený') : tt('Schválenie odobraté'),
        description: '',
      })
      onChanged()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, ''),
      })
    }
  }

  return (
    <div className="bg-card border border-border rounded-2xl px-4 py-3 space-y-2" data-external-driver={driver.id}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm truncate">{driver.name}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {[driver.phone, driver.email].filter(Boolean).join(' · ') || '-'}
          </p>
        </div>

        <GateBadge gate={gate} />

        <span className="text-right shrink-0">
          <span className="block text-sm font-bold tabular-nums">
            {driver.deliveryCount ?? 0} <T>rozvozov</T>
          </span>
          <span className="block text-[11px] text-muted-foreground tabular-nums">
            {formatDecimal(driver.totalKm ?? 0, 1)} km
          </span>
        </span>

        <span className="text-right shrink-0">
          <span className="block text-base font-black text-amber-500 tabular-nums" data-driver-reward>
            {formatMoney(driver.reward)}
          </span>
          <span className="block text-[11px] text-muted-foreground tabular-nums" data-driver-cash>
            <T>hotovosť</T> {formatMoney(driver.cashCollected ?? 0)}
          </span>
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/60">
        <span className="flex items-center gap-1 text-xs font-semibold" data-driver-balance>
          <HandCoins className="w-3.5 h-3.5 text-muted-foreground" />
          {balanceRead.isLoading
            ? tt('Zostatok sa načítava…')
            : balance === null
              ? tt('Zostatok sa nepodarilo načítať')
              : balanceStateNote(balance.owesCents)}
        </span>
        {balance !== null && balance.owesCents !== 0 && (
          <button
            type="button"
            data-driver-settle
            onClick={() => setSettling(true)}
            className="px-2.5 py-1 rounded-lg border border-border text-xs font-semibold"
          >
            <T>Vyrovnať</T>
          </button>
        )}

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => void setApproval(driver.adminApproved !== true)}
          title={
            canUnblock
              ? tt('Schválením sa šofér bude môcť prihlásiť.')
              : gate === 'blocked' || gate === 'awaiting-email'
                ? tt('Šofér ešte nepotvrdil email - schválenie ho zatiaľ nepustí dnu.')
                : undefined
          }
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors',
            driver.adminApproved
              ? 'border-border text-muted-foreground hover:text-foreground'
              : 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/25',
          )}
        >
          {driver.adminApproved ? (
            <>
              <ShieldAlert className="w-3.5 h-3.5" /> <T>Odobrať schválenie</T>
            </>
          ) : (
            <>
              <ShieldCheck className="w-3.5 h-3.5" /> <T>Schváliť</T>
            </>
          )}
        </button>
      </div>

      {settling && driver.id !== undefined && (
        <CourierSettleDialog
          driverId={driver.id}
          driverName={driver.name ?? ''}
          orderIds={[]}
          onClose={() => {
            setSettling(false)
            void balanceRead.refetch()
          }}
        />
      )}
    </div>
  )
}

const GATE_LABELS: Record<string, { label: string; className: string; icon: typeof MailCheck }> = {
  ready: {
    label: tt('môže sa prihlásiť'),
    className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/25',
    icon: ShieldCheck,
  },
  'awaiting-approval': {
    label: tt('čaká na schválenie'),
    className: 'bg-amber-500/10 text-amber-600 border-amber-500/25',
    icon: ShieldAlert,
  },
  'awaiting-email': {
    label: tt('nepotvrdil email'),
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/25',
    icon: MailCheck,
  },
  blocked: {
    label: tt('nepotvrdil email ani schválenie'),
    className: 'bg-red-500/10 text-red-600 border-red-500/25',
    icon: ShieldAlert,
  },
}

function GateBadge({ gate }: { gate: string }) {
  const style = GATE_LABELS[gate] ?? GATE_LABELS.blocked
  if (!style) return null
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold shrink-0',
        style.className,
      )}
    >
      <style.icon className="w-3 h-3" />
      {style.label}
    </span>
  )
}
